import React from 'react';
import { Navigate, useParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { buildAppPath } from '../../utils/navigation';

interface RoleGuardProps {
  children: React.ReactNode;
  roles: string[]; // Les rôles autorisés
  permissions?: string[]; // Les permissions requises (optionnel)
  excludedRoles?: string[]; // Les rôles exclus (optionnel)
}

const RoleGuard: React.FC<RoleGuardProps> = ({ children, roles, permissions = [], excludedRoles = [] }) => {
  const { authState, hasRole, can } = useAuth();
  const { companySlug } = useParams<{ companySlug?: string }>();

  // Vérifier si l'utilisateur est authentifié
  if (!authState.isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Vérifier si l'utilisateur a un rôle exclu
  if (excludedRoles.length > 0 && hasRole(excludedRoles)) {
    return <Navigate to="/unauthorized" replace />;
  }

  // Vérifier si l'utilisateur a le bon rôle
  const hasValidRole = hasRole(roles);

  // Vérifier si l'utilisateur a les permissions requises
  const hasRequiredPermissions = permissions.every(permission => can(permission));

  // Si l'utilisateur n'a pas le bon rôle ou les permissions requises, rediriger vers /unauthorized
  if (!hasValidRole || !hasRequiredPermissions) {
    return <Navigate to="/unauthorized" replace />;
  }

  if (
    companySlug &&
    authState.user &&
    authState.user.role !== 'super_admin' &&
    authState.company?.slug &&
    companySlug !== authState.company.slug
  ) {
    const fallbackRoute =
      authState.user.role === 'agent'
        ? 'weighing'
        : authState.user.role === 'accounting'
          ? 'reports'
          : 'dashboard';
    return <Navigate to={buildAppPath(fallbackRoute, authState)} replace />;
  }

  // Si tout est valide, afficher le contenu
  return <>{children}</>;
};

export default RoleGuard;
